<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import wootConstants from 'dashboard/constants/globals';
import { READ_STATUS } from 'dashboard/constants/gch';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';

const props = defineProps({
  status: { type: String, required: true },
  readStatus: { type: String, required: true },
  labels: { type: Array, required: true },
  // [{ key, title, labels: [{ title, description, color }] }]
  labelGroups: { type: Array, required: true },
});

const emit = defineEmits(['apply', 'close']);

const { t } = useI18n();

const draftStatus = ref(props.status);
const draftReadStatus = ref(props.readStatus);
const draftLabels = ref([...props.labels]);

const readStatusOptions = computed(() =>
  [READ_STATUS.READ, READ_STATUS.UNREAD, READ_STATUS.ALL].map(value => ({
    value,
    label: t(`CHAT_LIST.FILTER_PANEL.READ_STATUS.${value}`),
  }))
);

const statusOptions = computed(() =>
  ['open', 'resolved', 'pending', 'snoozed', 'all'].map(value => ({
    value,
    label: t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${value}.TEXT`),
  }))
);

const isLabelSelected = title => draftLabels.value.includes(title);

const toggleLabel = title => {
  draftLabels.value = isLabelSelected(title)
    ? draftLabels.value.filter(label => label !== title)
    : [...draftLabels.value, title];
};

const applyFilters = () => {
  emit('apply', {
    status: draftStatus.value,
    readStatus: draftReadStatus.value,
    labels: draftLabels.value,
  });
};

const onApply = () => {
  applyFilters();
  emit('close');
};

const onClear = () => {
  draftStatus.value = wootConstants.STATUS_TYPE.OPEN;
  draftReadStatus.value = READ_STATUS.ALL;
  draftLabels.value = [];
  applyFilters();
};
</script>

<template>
  <div
    class="flex flex-col gap-5 p-6 border shadow-lg w-[min(37.5rem,calc(100vw-2rem))] bg-n-alpha-3 backdrop-blur-[100px] border-n-weak rounded-xl"
  >
    <h2 class="text-base font-medium text-n-slate-12">
      {{ $t('FILTER.TITLE') }}
    </h2>
    <div class="grid grid-cols-2 gap-3">
      <label class="flex flex-col gap-2 min-w-0">
        <span class="text-sm text-n-slate-11">
          {{ $t('CHAT_LIST.FILTER_PANEL.READ_STATUS.LABEL') }}
        </span>
        <Select
          v-model="draftReadStatus"
          :options="readStatusOptions"
          class="!w-full [&>select]:w-full"
        />
      </label>
      <label class="flex flex-col gap-2 min-w-0">
        <span class="text-sm text-n-slate-11">
          {{ $t('CHAT_LIST.CHAT_SORT.STATUS') }}
        </span>
        <Select
          v-model="draftStatus"
          :options="statusOptions"
          class="!w-full [&>select]:w-full"
        />
      </label>
    </div>
    <div v-if="labelGroups.length" class="flex flex-col gap-2">
      <span class="text-sm text-n-slate-11">
        {{ $t('SIDEBAR.LABELS') }}
      </span>
      <div
        v-for="group in labelGroups"
        :key="group.key"
        class="flex flex-col gap-2"
      >
        <span
          v-if="labelGroups.length > 1"
          class="text-xs font-medium tracking-wide uppercase text-n-slate-10"
        >
          {{ group.title }}
        </span>
        <div
          class="grid grid-cols-[repeat(auto-fill,minmax(9.5rem,1fr))] gap-2"
        >
          <button
            v-for="label in group.labels"
            :key="label.title"
            type="button"
            class="flex items-center gap-2 px-2.5 py-1.5 text-sm rounded-lg outline outline-1 -outline-offset-1 min-h-9 text-start hover:outline-n-blue-9"
            :class="
              isLabelSelected(label.title)
                ? 'bg-n-blue-3 outline-n-blue-9 text-n-slate-12'
                : 'bg-n-alpha-2 outline-n-weak text-n-slate-11'
            "
            :aria-pressed="isLabelSelected(label.title)"
            @click="toggleLabel(label.title)"
          >
            <span class="flex-1 min-w-0 truncate">
              {{ label.description || label.title }}
            </span>
          </button>
        </div>
      </div>
    </div>
    <div class="flex justify-end gap-2">
      <NextButton
        slate
        faded
        sm
        :label="$t('FILTER.CLEAR_BUTTON_LABEL')"
        @click="onClear"
      />
      <NextButton
        sm
        :label="$t('FILTER.SUBMIT_BUTTON_LABEL')"
        @click="onApply"
      />
    </div>
  </div>
</template>
